import { Component, input, output, OnChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

interface EyeDropperResult {
  sRGBHex: string;
}

interface EyeDropperInstance {
  open: () => Promise<EyeDropperResult>;
}

type EyeDropperConstructor = new () => EyeDropperInstance;

declare global {
  interface Window {
    // here the naming convention needs to be disabled in order to use the browsers api, since camelCase does not work
    // eslint-disable-next-line @typescript-eslint/naming-convention
    EyeDropper?: EyeDropperConstructor;
  }
}

@Component({
  selector: 'app-color-picker',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    TranslocoDirective,
  ],
  templateUrl: './color-picker.component.html',
  styleUrl: './color-picker.component.css',
})
export class ColorPickerComponent implements OnDestroy, OnChanges {
  tooltip = input<string>('');
  color = input<string>('');
  colorChange = output<string>();
  inputElement: HTMLInputElement;

  private readonly defaultColor = '#ff9100';

  isHexInvalid = false;
  colorHexValue = this.defaultColor;
  textHexValue = this.colorHexValue.substring(1);
  isPipetteActive = false;

  constructor() {
    this.setupClickListener();
  }

  ngOnChanges(): void {
    if (this.color()) {
      this.colorHexValue = this.color();
      this.textHexValue = this.color().substring(1);
      this.isHexInvalid = false;
    }
  }

  updateTextInput() {
    this.textHexValue = this.colorHexValue.substring(1);
    this.colorChange.emit(this.colorHexValue);
  }

  onHexInput(event: Event): void {
    const inputField = event.target as HTMLInputElement;
    const filtered = inputField.value.replace(/[^0-9a-fA-F]/g, '');
    if (filtered !== inputField.value) {
      this.textHexValue = filtered;
      inputField.value = filtered;
    }
  }

  onColorPickerChange(value: string): void {
    this.textHexValue = value.substring(1);
    this.isHexInvalid = false;
    this.colorChange.emit(value);
  }

  updateColorPicker(value: string) {
    value = value.toUpperCase();

    const hexPattern = /^[0-9A-F]{6}$/;

    if (value.length < 6) {
      this.isHexInvalid = true;
    } else if (hexPattern.test(value)) {
      this.isHexInvalid = false;
      this.colorHexValue = '#' + value;
      this.colorChange.emit(this.colorHexValue);
    } else {
      this.isHexInvalid = true;
    }
  }

  async togglePipette(): Promise<void> {
    if (window.EyeDropper) {
      this.isPipetteActive = true;

      try {
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();
        const hexcode = result.sRGBHex.toUpperCase();

        this.colorHexValue = hexcode;
        this.textHexValue = hexcode.substring(1);
        this.isHexInvalid = false;
        this.colorChange.emit(this.colorHexValue);
      } catch {
        // User canceled the eyedropper (e.g. pressed Escape)
      } finally {
        this.isPipetteActive = false;
      }
    } else {
      this.isPipetteActive = !this.isPipetteActive;
      document.body.classList.toggle('pipette-active', this.isPipetteActive);
    }
  }

  setupClickListener(): void {
    document.addEventListener('click', (event: MouseEvent) => {
      this.onPictureClick(event);
    });
  }

  onPictureClick(event: MouseEvent): void {
    if (!this.isPipetteActive) {
      return;
    }

    const target = event.target as HTMLElement;

    if (target instanceof HTMLImageElement) {
      this.getColorFromImage(event);
      event.stopPropagation();
    }
  }

  getColorFromImage(event: MouseEvent): void {
    const target = event.target as HTMLImageElement;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = target.naturalWidth;
    canvas.height = target.naturalHeight;

    const rect = target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const scaleX = target.naturalWidth / rect.width;
    const scaleY = target.naturalHeight / rect.height;

    if (ctx) {
      ctx.drawImage(target, 0, 0);
    } else {
      void this.togglePipette();
      return;
    }

    const imageData = ctx.getImageData(x * scaleX, y * scaleY, 1, 1);
    const [r, g, b] = imageData.data;
    const hexcode =
      '#' +
      [r, g, b]
        .map((val) => val.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();

    this.colorHexValue = hexcode;
    this.textHexValue = hexcode.substring(1);
    this.isHexInvalid = false;
    this.colorChange.emit(this.colorHexValue);
    // turn pipette off again
    this.disablePipette();
  }

  private disablePipette() {
    this.isPipetteActive = false;
    document.body.classList.remove('pipette-active');
  }

  ngOnDestroy(): void {
    this.disablePipette();
  }
}
